<script setup lang="ts">
import { useData } from "vitepress";
import { useNav } from "../composables/useNav";
import { Icon } from "../primitives/icons";

const { theme } = useData();
const { isScreenOpen, closeScreen } = useNav();
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isScreenOpen"
        id="nav-screen"
        class="my-nav-screen"
        @click.self="closeScreen"
      >
        <div class="my-nav-screen-container">
          <button
            class="my-nav-screen-close"
            type="button"
            aria-label="Close navigation menu"
            @click="closeScreen"
          >
            <Icon name="close" :size="24" />
          </button>

          <nav class="my-nav-screen-menu" aria-label="Mobile navigation">
            <a
              v-for="link in theme.nav"
              :key="link.link"
              :href="link.link"
              class="my-nav-screen-link"
              @click="closeScreen"
            >
              {{ link.text }}
            </a>
          </nav>

          <div v-if="theme.socialLinks" class="my-nav-screen-social">
            <a
              v-for="social in theme.socialLinks"
              :key="social.link"
              :href="social.link"
              class="my-nav-screen-social-link"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="social.icon"
            >
              <Icon :name="social.icon" :size="24" />
            </a>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.my-nav-screen {
  position: fixed;
  inset: 0;
  z-index: 200;
  background-color: var(--color-background);
  backdrop-filter: blur(40px);
}

.my-nav-screen-container {
  display: flex;
  flex-direction: column;
  padding: var(--space-xl);
  height: 100%;
}

.my-nav-screen-close {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  width: 48px;
  height: 48px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-background-soft);
  }
}

.my-nav-screen-menu {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  margin-top: var(--space-2xl);
}

.my-nav-screen-link {
  font-size: var(--font-size-2xl);
  font-weight: 600;
  color: inherit;
  text-decoration: none;
  padding: var(--space-md) 0;
  border-bottom: 1px solid var(--color-border);
  transition: color 0.2s;

  &:hover {
    color: var(--color-accent);
  }
}

.my-nav-screen-social {
  display: flex;
  gap: var(--space-lg);
  margin-top: auto;
  padding-top: var(--space-xl);
}

.my-nav-screen-social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  color: var(--color-text-muted);
  transition: color 0.2s;

  &:hover {
    color: var(--color-text);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
